import React, { useMemo } from 'react';
import { always, equals, ifElse, isNil, pathOr, prop, take, sum } from 'ramda';
import { Group } from '@vx/group';
import { ParentSize } from '@vx/responsive';
import { scaleBand, scaleLinear, scaleOrdinal } from '@vx/scale';
import { useTooltipInPortal } from '@vx/tooltip';

import { useIntl } from 'gatsby-plugin-intl';
import { Grid } from '../grid';
import { Axes } from '../axes';
import { NoData, isNoData } from '../noData';
import { Header } from '../header';
import { parseData } from '../charts.helper';
import { CHART_TYPES, DEFAULT_SIZE } from '../charts.constants';
import { BAR_DEFAULT } from '../configs/bar.config';
import { Container, Chart, Svg, Legend } from '../charts.styles';
import { useChartTooltipContext } from '../../../store/ChartTooltipContext';
import { renderOpenTooltip } from '../tooltipBasic/tooltipBasic.utils';
import { TooltipWithPortal } from '../tooltipWithPortal';
import { toFixed } from '../../../utils/math';

import { TooltipElementTable, TooltipTableDataCell } from '../../tooltipElementTable/tooltipElementTable.styles';
import { StackItem } from './stackChart.styles';
import { useGatsbyContext } from '../../../store/GatsbyContext';
import { findPiLabel } from '../../../utils/findPiLabel';

const standarizeDashes = (item) => item && item.replace(/[^a-z0-9]/gi, '');
const standarize = (array) => array.map(standarizeDashes);

const findMax = (data, series) => {
  const values = data.map((item) => sum(series.map((serie) => item[serie] || 0)));

  return Math.max(...values);
};

const getScales = ({ chartWidth, chartHeight, type, data, series, axis, config: { padding }, maxXAxisScore }) => {
  const getAxis = prop(axis);

  const stackScale = scaleBand({
    domain: data.map(getAxis),
    padding,
  });

  const maxValue = type === CHART_TYPES.HORIZONTAL && maxXAxisScore ? maxXAxisScore : findMax(data, series);
  const valueScale = scaleLinear({
    domain: [0, maxValue > 4 ? maxValue : 4],
    nice: true,
  });

  const { stackScalesProps, gridScalesProps } = ifElse(
    equals(CHART_TYPES.HORIZONTAL),
    () => {
      stackScale.rangeRound([0, chartHeight]);
      valueScale.range([0, chartWidth]);

      return {
        stackScalesProps: {
          y: getAxis,
          yScale: stackScale,
          xScale: valueScale,
        },
        gridScalesProps: {
          xScale: valueScale,
        },
      };
    },
    () => {
      stackScale.rangeRound([0, chartWidth]);
      valueScale.range([chartHeight, 0]);

      return {
        stackScalesProps: {
          x: getAxis,
          xScale: stackScale,
          yScale: valueScale,
        },
        gridScalesProps: {
          yScale: valueScale,
        },
      };
    }
  )(type);

  const axesScalesProps = {
    groupScale: stackScale,
    valueScale,
  };

  return { axesScalesProps, stackScalesProps, gridScalesProps };
};

export const StackChart = ({
  width = DEFAULT_SIZE,
  height = DEFAULT_SIZE,
  chartData = {},
  tabs,
  config: customConfig = {},
  type,
  customParser,
  stackComponent: StackComponent,
}) => {
  if (customParser) customParser(chartData);

  const { title, subtitle, axis, longName, data: rawData, labels = {}, series, maxXAxisScore = null } = chartData;
  if (isNoData(rawData, axis, series)) return <NoData width={width} height={height} />;

  const data = parseData(rawData, series);
  const tooltip = useChartTooltipContext();
  const { containerRef } = useTooltipInPortal();

  const total = useMemo(() => series.reduce((sum, serie) => tooltip.data && sum + tooltip.data[serie], 0), [
    series,
    tooltip.data,
  ]);

  const config = {
    ...BAR_DEFAULT,
    ...customConfig,
  };

  const color = scaleOrdinal({
    domain: series,
    range: take(series.length, config.colors),
  });

  const yOffset = config.yAxisVisible ? config.yAxisSpace : 0;
  const xOffset = config.xAxisVisible ? config.xAxisSpace : 0;

  const handleHover = ({ index }) => (e) => {
    const stackRect = e.target.getBoundingClientRect();
    const top = stackRect.top;
    const left = stackRect.left;

    if (left === tooltip.left && top === tooltip.top) {
      return;
    }

    tooltip.show({
      data: { ...data[index], index },
      top: top,
      left: left,
    });
  };

  const renderTooltipContent = () => {
    const VALUE_COLUMN_NAME = 'Number';
    const TOTAL_NAME = 'Total';
    const { piLabels } = useGatsbyContext();
    const piLabelsIndicators = piLabels.map(prop('indicator'));
    const { formatMessage } = useIntl();

    const itemsByIndicator = Object.values(labels).filter((label) =>
      standarize(piLabelsIndicators).includes(standarizeDashes(label))
    );

    const getValueByIndicator = (serie, index) =>
      itemsByIndicator.length > 0
        ? `${itemsByIndicator[index]}: ${findPiLabel(piLabels, labels[serie])}`
        : labels[serie];

    return (
      tooltip.data && (
        <TooltipElementTable>
          <thead>
            <tr>
              <th>{formatMessage({ id: tooltip.data[longName] || tooltip.data[axis] })}</th>
              <th>{formatMessage({ id: VALUE_COLUMN_NAME })}</th>
            </tr>
          </thead>
          <tbody>
            {series.map(
              (serie, index) =>
                !isNil(tooltip.data[serie]) && (
                  <tr key={`${serie}-${index}`}>
                    <TooltipTableDataCell>
                      {formatMessage({ id: getValueByIndicator(serie, index) })}
                    </TooltipTableDataCell>
                    <TooltipTableDataCell color={config.colors[index]}>
                      <span>{toFixed(tooltip.data[serie], 1)}</span>
                    </TooltipTableDataCell>
                  </tr>
                )
            )}
            <tr>
              <TooltipTableDataCell>{formatMessage({ id: TOTAL_NAME })}</TooltipTableDataCell>
              <TooltipTableDataCell>
                <span>{total}</span>
              </TooltipTableDataCell>
            </tr>
          </tbody>
        </TooltipElementTable>
      )
    );
  };

  const hoveredGroupIndex = pathOr(null, ['data', 'index'], tooltip);
  const isHoverActive = !isNil(hoveredGroupIndex);

  return (
    <Container width={width}>
      <Header title={title} subtitle={subtitle} xOffset={xOffset} tabs={tabs} config={config}>
        <Legend colorScale={color} right={0} labels={labels} />
      </Header>
      <Chart width={width} height={height} ref={containerRef}>
        {renderOpenTooltip(always(<TooltipWithPortal>{renderTooltipContent()}</TooltipWithPortal>), tooltip)}
        <ParentSize>
          {({ width: chartAreaWidth, height: chartAreaHeight }) => {
            if (!chartAreaWidth || !chartAreaHeight) return null;

            const chartWidth = chartAreaWidth - xOffset;
            const chartHeight = chartAreaHeight - yOffset;
            const { axesScalesProps, stackScalesProps, gridScalesProps } = getScales({
              chartWidth,
              chartHeight,
              type,
              data,
              series,
              axis,
              config,
              maxXAxisScore,
            });

            return (
              <Svg>
                <Grid left={xOffset} width={chartWidth} height={chartHeight} config={config} {...gridScalesProps}>
                  <Group>
                    <StackComponent data={data} keys={series} color={color} {...stackScalesProps}>
                      {(barStacks) =>
                        barStacks.map((barStack) =>
                          barStack.bars.map((bar, index) => (
                            <StackItem
                              key={`bar-stack-${barStack.index}-${bar.index}`}
                              x={bar.x}
                              y={bar.y}
                              height={bar.height}
                              width={bar.width}
                              fill={bar.color}
                              isHoverActive={isHoverActive}
                              isGroupHovered={hoveredGroupIndex === index}
                              {...tooltip.bindHoverEvents(handleHover({ index }))}
                            />
                          ))
                        )
                      }
                    </StackComponent>
                  </Group>
                </Grid>
                <Axes
                  type={type}
                  chartHeight={chartHeight}
                  chartWidth={chartWidth}
                  config={config}
                  {...axesScalesProps}
                />
              </Svg>
            );
          }}
        </ParentSize>
      </Chart>
    </Container>
  );
};
